import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { AddIcon, InfoIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../../../../hooks/isMobile';
import { CLASSROOM_CONFIG } from '../config';

interface ClassroomInfoCardProps {
  onCreateClassroom: () => void;
  canCreate: boolean;
}

export const ClassroomInfoCard: React.FC<ClassroomInfoCardProps> = ({
  onCreateClassroom,
  canCreate,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const bgColor = '#ebf8ff';
  const borderColor = '#90cdf4';
  const textColor = '#2c5282';

  if (!canCreate) {
    return null;
  }

  return (
    <Box
      bg={bgColor}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={isMobile ? 4 : 6}
      mb={6}
    >
      <HStack spacing={4} align="start">
        <Icon
          as={InfoIcon}
          w={6}
          h={6}
          color="blue.500"
          mt={1}
          flexShrink={0}
        />
        
        <VStack align="start" spacing={3} flex={1} minW={0}>
          <VStack align="start" spacing={1}>
            <Text
              fontSize="lg"
              fontWeight="semibold"
              color={textColor}
            >
              {t('classroom.createNewClassroom')}
            </Text>
            
            <Text
              fontSize="sm"
              color={textColor}
              opacity={0.8}
              wordBreak={isMobile ? "break-word" : "normal"}
              lineHeight={isMobile ? "1.4" : "normal"}
            >
              {t('classroom.setupClassroomsDescription')}
            </Text>
          </VStack>

          {isMobile ? (
            <VStack spacing={3} align="start" w="full">
              <Button
                colorScheme="blue"
                size="sm"
                leftIcon={<AddIcon />}
                onClick={onCreateClassroom}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
                w="full"
              >
                {t('classroom.createClassroom')}
              </Button>
              
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                whiteSpace="nowrap"
              >
                {t('classroom.availablePrograms', { count: CLASSROOM_CONFIG.AVAILABLE_PROGRAMS.length })}
              </Badge>
            </VStack>
          ) : (
            <HStack spacing={3}>
              <Button
                colorScheme="blue"
                size="md"
                leftIcon={<AddIcon />}
                onClick={onCreateClassroom}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md',
                }}
                transition="all 0.2s"
              >
                {t('classroom.createClassroom')}
              </Button>
              
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                {t('classroom.availablePrograms', { count: CLASSROOM_CONFIG.AVAILABLE_PROGRAMS.length })}
              </Badge>
            </HStack>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};
